import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, PageHeader, Skeleton, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { DriverAvatar, DriverDialog, driverTone, type DriverRow } from './DriversPage';

interface DriverDetail extends DriverRow {
  createdAt: string;
  currentVehicle: { id: string; plateAr: string; plateLatin: string | null; make: string | null; model: string | null } | null;
  assignments: { id: string; startedAt: string; endedAt: string | null; vehicle: { id: string; plateAr: string } }[];
}

export function DriverDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ['driver', id], queryFn: () => api<DriverDetail>(`/drivers/${id}`) });
  const setStatus = useMutation({ mutationFn: (status: string) => api(`/drivers/${id}`, { method: 'PATCH', json: { status } }), onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['driver', id] }); void qc.invalidateQueries({ queryKey: ['drivers'] }); }, onError: toast.error });
  const remove = useMutation({ mutationFn: () => api(`/drivers/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); nav('/drivers'); }, onError: toast.error });

  if (q.isLoading || !q.data) return <Skeleton className="h-96" />;
  const d = q.data;
  return (
    <div className="flex flex-col gap-5">
      <Link to="/drivers" className="flex items-center gap-1 text-sm text-text-muted hover:text-text"><ArrowRight size={14} className="ltr:rotate-180" /> {t('nav.drivers')}</Link>
      <PageHeader
        title={<span className="flex items-center gap-3"><DriverAvatar name={d.fullName} photoUrl={d.photoUrl} size="lg" />{d.fullName}</span>}
        subtitle={d.phone}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={driverTone(d.status)} dot>{t(`drivers.statuses.${d.status}`)}</Badge>
            <Can perm="drivers:update">
              <select className="input !w-auto !min-h-9 !py-1.5" value={d.status} onChange={(e) => setStatus.mutate(e.target.value)} aria-label={t('settings.users.status')}>
                {(['ACTIVE', 'ON_LEAVE', 'SUSPENDED'] as const).map((s) => <option key={s} value={s}>{t(`drivers.statuses.${s}`)}</option>)}
              </select>
              <button className="btn-secondary" onClick={() => setEditing(true)}><Pencil size={16} /> {t('common.edit')}</button>
            </Can>
            <Can perm="drivers:delete"><button className="btn-ghost text-danger-fg" onClick={() => confirmDialog(t('drivers.deleteConfirm', { name: d.fullName }), { danger: true }).then((ok) => ok && remove.mutate())}><Trash2 size={16} /> {t('common.delete')}</button></Can>
          </div>
        }
      />
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card p-5">
          <h3 className="mb-3 text-sm font-bold">{t('vehicles.details')}</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-text-muted">{t('drivers.vehicle')}</dt><dd className="font-medium">{d.currentVehicle ? <Link to={`/vehicles/${d.currentVehicle.id}`} className="hover:underline">{d.currentVehicle.plateAr}</Link> : '—'}</dd>
            <dt className="text-text-muted">{t('drivers.nationalId')}</dt><dd className="font-medium" dir="ltr">{d.nationalId ?? '—'}</dd>
            <dt className="text-text-muted">{t('drivers.licenseNumber')}</dt><dd className="font-medium" dir="ltr">{d.licenseNumber ?? '—'}</dd>
            <dt className="text-text-muted">{t('drivers.licenseClass')}</dt><dd className="font-medium">{d.licenseClass ?? '—'}</dd>
            <dt className="text-text-muted">{t('drivers.licenseExpiresAt')}</dt><dd className="font-medium">{fmtDate(d.licenseExpiresAt, i18n.language)}</dd>
            <dt className="text-text-muted">{t('drivers.hiredAt')}</dt><dd className="font-medium">{fmtDate(d.hiredAt, i18n.language)}</dd>
            <dt className="text-text-muted">{t('drivers.lastLogin')}</dt><dd className="font-medium">{d.user?.lastLoginAt ? fmtDateTime(d.user.lastLoginAt, i18n.language) : t('drivers.neverLoggedIn')}</dd>
          </dl>
          {d.notes ? <p className="mt-3 rounded-md bg-surface-2 p-3 text-sm text-text-2">{d.notes}</p> : null}
        </section>
        <section className="card p-5">
          <h3 className="mb-3 text-sm font-bold">{t('vehicles.assignmentHistory')}</h3>
          {!d.assignments.length ? <p className="text-sm text-text-muted">{t('common.empty')}</p> : (
            <ul className="divide-y divide-border-soft text-sm">
              {d.assignments.map((a) => <li key={a.id} className="flex items-center justify-between py-2"><Link to={`/vehicles/${a.vehicle.id}`} className="font-medium hover:underline">{a.vehicle.plateAr}</Link><span className="text-xs text-text-muted">{fmtDateTime(a.startedAt, i18n.language)} → {a.endedAt ? fmtDateTime(a.endedAt, i18n.language) : <Badge tone="success">{t('vehicles.current')}</Badge>}</span></li>)}
            </ul>
          )}
        </section>
      </div>
      {editing ? <DriverDialog driver={d} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}
